import { SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { Outputs } from '@/_models/virtualStaging/VRPointer/component/types';
import { ControllerType } from '@/_models/virtualStaging/HTMLMesh/component/Menu/types';

interface GamepadEvent {
  controller: ControllerType;
  button: number;
  type: string;
}

export class VREventManager extends SceneComponent {
  public root: any = null;

  public pivot: any = null;

  public hostPose: any;

  public eventListeners = [];

  controllers: any;

  public controllerL: any;

  public controllerR: any;

  public gamepadL: any;

  public gamepadR: any;

  public activeButtons = {
    gamepadL: new Map(),
    gamepadR: new Map(),
  };

  public activeAxes = {
    gamepadL: new Map(),
    gamepadR: new Map(),
  };

  inputs: any = {
    visible: false,
  };

  outputs: Outputs = {
    loadingState: LoadingState.Loaded,
  };

  declare context: any;

  onInit() {
    this.getXRController();
  }

  getXRController() {
    const { xr } = this.context.renderer;

    this.controllers = [xr.getController(0), xr.getController(1)];

    this.controllers.forEach((controller) => {
      controller.addEventListener('connected', (event) => {
        const inputSource = event.data;
        const { handedness } = inputSource;

        if (handedness === 'left') {
          this.controllerL = controller;
          this.gamepadL = inputSource.gamepad;
        } else if (handedness === 'right') {
          this.controllerR = controller;
          this.gamepadR = inputSource.gamepad;
        }
      });
    });
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    this.root.visible = this.inputs.visible;
  }

  addEventListener(event: GamepadEvent, callback: any) {
    this.eventListeners.push({ event, callback });
  }

  removeEventListener(event: GamepadEvent, callback: any) {
    const listenerToRemove = this.eventListeners.find((listener) => listener.callback === callback);
    if (listenerToRemove)
      this.eventListeners.splice(this.eventListeners.indexOf(listenerToRemove), 1);
  }

  cycleVR(_session) {
    if (this.gamepadL) {
      this.gamepadL.buttons.forEach((button, index) => {
        if (button.pressed && !this.activeButtons.gamepadL.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'pointerdown',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        if (!button.pressed && this.activeButtons.gamepadL.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'pointerup',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        this.activeButtons.gamepadL.set(index, button.pressed);
      });

      this.gamepadL.axes.forEach((axis, index) => {
        if (!this.activeAxes.gamepadL.get(index) && (axis < -0.5 || axis > 0.5)) {
          this.activeAxes.gamepadL.set(index, true);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholddown',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
        if (this.activeAxes.gamepadL.get(index) && axis >= -0.5 && axis <= 0.5) {
          this.activeAxes.gamepadL.set(index, false);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadL &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholdup',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
      });
    }
    if (this.gamepadR) {
      this.gamepadR.buttons.forEach((button, index) => {
        if (button.pressed && !this.activeButtons.gamepadR.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'pointerdown',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        if (!button.pressed && this.activeButtons.gamepadR.get(index)) {
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'pointerup',
          );
          listeners.forEach((listener) => listener.callback(button));
        }
        this.activeButtons.gamepadR.set(index, button.pressed);
      });

      this.gamepadR.axes.forEach((axis, index) => {
        if (!this.activeAxes.gamepadR.get(index) && (axis < -0.5 || axis > 0.5)) {
          this.activeAxes.gamepadR.set(index, true);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholddown',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
        if (this.activeAxes.gamepadR.get(index) && axis >= -0.5 && axis <= 0.5) {
          this.activeAxes.gamepadR.set(index, false);
          const listeners = this.eventListeners.filter(
            (listener) =>
              listener.event.controller === ControllerType.GamepadR &&
              listener.event.button === index &&
              listener.event.type === 'thumbstickthreshholdup',
          );
          listeners.forEach((listener) => listener.callback(axis));
        }
      });
    }
  }

  onTick() {
    if (this.context?.renderer?.xr?.isPresenting) {
      const _session = this.context.renderer.xr.getSession();
      _session.requestAnimationFrame(() => this.cycleVR(_session));
    }
  }
}

export const VREventManagerComponentType = 'mp.VRPointerComponent';
export const makeVREventManagerComponent = (): any => new VREventManager();
